import { v4 as uuid } from 'uuid'

import {
	BAR_ITEM,
	EVENT,
	MARKETING_CONTENT_CATEGORIES,
	MARKETING_CONTENT_STATUS,
	PARTNERSHIP
} from '../../../utils/marketing'

/** A record the user has added but not saved yet — it has no id until the POST returns. */
export const isNewRecord = (record) => !record || !record._id

/**
 * The shape of a fresh record: a draft with empty content. Records are created server-side
 * (create-on-add), so this is used only to derive CONTENT_FIELDS.
 */
export const emptyRecord = (category) => ({
	// Stable client key so a new card keeps its React key — and its in-progress edits — when the
	// list reindexes around it before the first save. Not a content field; never sent to the API.
	_tempId: uuid(),
	category,
	status: MARKETING_CONTENT_STATUS.DRAFT,
	headline: '',
	subheadline: '',
	description: '',
	pill_text: '',
	action_text: '',
	external_link: '',
	in_app_link_stack: '',
	in_app_link_screen: '',
	image_url: '',
	partner_logo_url: ''
})

/**
 * The fields the editor owns and Save writes. status and order are server-managed (via the
 * toggle and reorder), so they are excluded: Save must not push a stale `status` over what the
 * toggle set, and the dirty check must not react to a server-owned change.
 */
export const CONTENT_FIELDS = Object.keys(emptyRecord('')).filter(
	(field) => field !== 'category' && field !== 'status' && !field.startsWith('_')
)

/** Link fields, last column in every category — in-app destination first, external last. */
const LINK_COLUMN = [
	{
		name: 'in_app_link_stack',
		label: 'In-App Destination',
		kind: 'inAppStack'
	},
	{
		name: 'in_app_link_screen',
		label: 'In-App Screen',
		kind: 'inAppScreen'
	},
	{
		name: 'external_link',
		label: 'External Link'
	}
]

export const CATEGORY_CONFIG = {
	event: {
		title: 'Events & Specials',
		allowMultiple: true,
		imageField: 'image_url',
		imageLabel: 'Event artwork',
		minWidth: 1260,
		minHeight: 630,
		ratioLabel: '2:1',
		previewStyle: EVENT,
		previewAspect: '2 / 1',
		columns: [
			[
				{ name: 'headline', label: 'Headline' },
				{ name: 'description', label: 'Description', multiline: true }
			],
			[
				{ name: 'subheadline', label: 'Footer text (left)' },
				{ name: 'action_text', label: 'Footer action (right)' }
			],
			LINK_COLUMN
		]
	},

	bar_item: {
		title: 'Featured Bar Items',
		// Only one bar item may be active at a time — the app shows a single one, not a carousel.
		allowMultiple: false,
		imageField: 'image_url',
		imageLabel: 'Bar & Park artwork',
		minWidth: 1260,
		minHeight: 840,
		ratioLabel: '3:2',
		previewStyle: BAR_ITEM,
		previewAspect: '3 / 2',
		columns: [
			[
				{ name: 'headline', label: 'Headline' },
				{ name: 'description', label: 'Description', multiline: true }
			],
			[
				{ name: 'subheadline', label: 'Subheadline' },
				{ name: 'action_text', label: 'Action Text' }
			],
			LINK_COLUMN
		]
	},

	partnership: {
		title: 'Offers',
		allowMultiple: true,
		imageField: 'partner_logo_url',
		imageLabel: 'Partner logo',
		minWidth: 360,
		minHeight: 240,
		previewStyle: PARTNERSHIP,
		columns: [
			[
				{ name: 'headline', label: 'Headline' },
				{ name: 'description', label: 'Description', multiline: true }
			],
			[
				{ name: 'pill_text', label: 'Badge' },
				{ name: 'action_text', label: 'Link Text' }
			],
			// Partnerships always link out, so the in-app fields are omitted rather than left empty.
			[{ name: 'external_link', label: 'External Link' }]
		]
	}
}

/**
 * Why a record can't go active yet, or null. Mirrors the API rule so the toggle can be disabled
 * with a reason instead of failing the save with a 400.
 */
export const blockingReason = (category, record) => {
	const config = CATEGORY_CONFIG[category]

	if (category === PARTNERSHIP && !record.external_link) {
		return 'Add an external link before making this active'
	}

	if (category !== PARTNERSHIP && !record[config.imageField]) {
		return `Add ${config.imageLabel.toLowerCase()} before making this active`
	}

	return null
}

/**
 * The reason a chosen image fails its category's requirements, or null if it passes — a truthy
 * result blocks the upload. Larger than the minimum is fine: ratio-locked categories are cropped
 * to a fixed shape, so an off-ratio or undersized file is rejected; a partner logo is fitted, so
 * only a genuinely tiny one is rejected.
 */
export const artworkDoesNotMeetRequirements = (category, width, height) => {
	const config = CATEGORY_CONFIG[category]

	if (!config || !config.minWidth || !width || !height) {
		return null
	}

	if (config.ratioLabel) {
		const target = config.minWidth / config.minHeight
		const actual = width / height
		const ratioOff = Math.abs(actual - target) > target * 0.01
		// Ratio holds, so height scales with width — one dimension check covers both.
		const tooSmall = width < config.minWidth

		if (ratioOff || tooSmall) {
			return `This image is ${width} x ${height}. ${config.imageLabel} must be ${config.ratioLabel} with a min size of ${config.minWidth} x ${config.minHeight}.`
		}

		return null
	}

	// A fitted logo can be any aspect, so reject only one smaller than the fit box in both dimensions.
	if (width < config.minWidth && height < config.minHeight) {
		return `This image is ${width} x ${height}. ${config.imageLabel} must be at least ${config.minWidth} x ${config.minHeight}.`
	}

	return null
}

export const CATEGORIES = MARKETING_CONTENT_CATEGORIES
